/**
 * Sidebar inbox unread badge count (TIER-2 fidelity parity).
 *
 * The legacy chrome carried an `#inbox-badge` on the Inbox nav-item; v2 shipped a
 * `badges` prop on the sidebar but never fed it, so the badge never appeared.
 * This hook supplies the count the `AppShell` passes to the sidebar.
 *
 * The "unread / needs-attention" signal is the count of INCOMING mail classified
 * as `new_project_candidate` (the amber "New Project" bucket the recruiter must
 * triage) — the same subset the Inbox surfaces as actionable. We reuse the
 * feature-owned `useInboxEmails` query (import only — `inbox/` is not edited) with
 * that classification filter and count the incoming rows across whatever pages are
 * loaded (a single 50-row page is almost always the whole bucket for a badge).
 *
 * Failures / loading degrade to `0` (the badge simply hides) — a badge must never
 * white-screen the authed shell.
 */
import { useMemo } from 'react';
import { useInboxEmails } from '../inbox/inboxApi.js';

/**
 * The unread/actionable inbox count for the sidebar badge. `enabled === false`
 * (no session) short-circuits to `0` without firing the query.
 */
export function useInboxBadgeCount(enabled: boolean = true): number {
  // NOTE: the hook is always called (Rules of Hooks); the query itself is gated by
  // `enabled` so it never fetches for an anonymous shell.
  const query = useInboxEmails({ classification: 'new_project_candidate' });

  return useMemo(() => {
    if (!enabled || !query.data) {
      return 0;
    }
    let count = 0;
    for (const page of query.data.pages) {
      for (const email of page.emails) {
        // Incoming only — a reply we sent (direction 'out') is never "unread".
        if ((email.direction ?? '') !== 'out') {
          count += 1;
        }
      }
    }
    return count;
  }, [enabled, query.data]);
}
